"use client";

import { useEffect, useRef } from "react";
import {
  resolveValue,
  Toaster,
  type Toast,
  useToasterStore,
} from "react-hot-toast";

export interface EnqueuedToast {
  sequence: number;
  id: string;
  type: Toast["type"];
  message: string;
}

declare global {
  interface Window {
    __PIMA_E2E_INSTRUMENTATION__?: boolean;
    _enqueuedToasts?: EnqueuedToast[];
  }
}

const MAX_RECORDED_TOASTS = 100;
export const TOAST_SEQUENCE_STORAGE_KEY = "__pima_e2e_toast_sequence__";

export interface ToastSequencePersistence {
  getSequence: () => number;
  setSequence: (sequence: number) => void;
}

export function sessionToastSequence(): ToastSequencePersistence {
  return {
    getSequence: () => {
      try {
        const raw = sessionStorage.getItem(TOAST_SEQUENCE_STORAGE_KEY);
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? parsed : 0;
      } catch {
        return 0;
      }
    },
    setSequence: (sequence) => {
      try {
        sessionStorage.setItem(TOAST_SEQUENCE_STORAGE_KEY, String(sequence));
      } catch {
        // about:blank / opaque origins deny storage; sequence stays in-memory.
      }
    },
  };
}

export function appendNewToasts(
  ledger: EnqueuedToast[],
  seenToastIds: Set<string>,
  toasts: Toast[],
  persistence?: ToastSequencePersistence,
): void {
  const presentIds = new Set(toasts.map((toast) => toast.id));
  for (const id of seenToastIds) {
    if (!presentIds.has(id)) seenToastIds.delete(id);
  }

  let sequence = Math.max(
    ledger.at(-1)?.sequence ?? 0,
    persistence?.getSequence() ?? 0,
  );
  // Store order is newest-first; for equal createdAt, prefer the older enqueue
  // (higher index) so sequence follows enqueue order, not display order.
  const newlyEnqueued = toasts
    .map((toast, index) => ({ toast, index }))
    .filter(({ toast }) => !seenToastIds.has(toast.id))
    .sort(
      (left, right) =>
        left.toast.createdAt - right.toast.createdAt ||
        right.index - left.index,
    )
    .map(({ toast }) => toast);

  for (const toast of newlyEnqueued) {
    seenToastIds.add(toast.id);
    const resolvedMessage = resolveValue(toast.message, toast);
    if (typeof resolvedMessage !== "string") continue;
    ledger.push({
      sequence: ++sequence,
      id: toast.id,
      type: toast.type,
      message: resolvedMessage,
    });
  }

  persistence?.setSequence(sequence);

  if (ledger.length > MAX_RECORDED_TOASTS) {
    ledger.splice(0, ledger.length - MAX_RECORDED_TOASTS);
  }
}

function E2EToastProbe() {
  const { toasts } = useToasterStore();
  const seenToastIds = useRef(new Set<string>());

  useEffect(() => {
    window._enqueuedToasts ??= [];
    appendNewToasts(
      window._enqueuedToasts,
      seenToastIds.current,
      toasts,
      sessionToastSequence(),
    );
  }, [toasts]);

  return null;
}

export function AppToaster() {
  return (
    <>
      {typeof window !== "undefined" &&
        window.__PIMA_E2E_INSTRUMENTATION__ === true && <E2EToastProbe />}
      <Toaster position="top-right" />
    </>
  );
}
